import React, { useState } from 'react';
import { message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  MasterDataEntityType,
  MasterDataTagType,
  useDeleteMasterDataMutation,
} from './masterdata-api';

/**
 * antd's message and Alert render whatever they are given as a React node, so a
 * non-string error body (e.g. ProblemDetails) would throw and trip the global
 * ErrorBoundary instead of surfacing the problem.
 */
export const toDisplayMessage = (value: any, fallback: string): string => {
  if (typeof value === 'string' && value.trim()) return value;
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'object') {
    const candidate = value.message ?? value.detail ?? value.title ?? value.error;
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
    return fallback;
  }
  return String(value);
};

export interface UseMasterDataDeleteOptions {
  /** EntityType passed to sp_DeleteMasterData. */
  entityType: MasterDataEntityType;
  /** Cache tag for the list being shown. */
  tagType: MasterDataTagType;
  /** Lower case singular name used in the confirmation and fallback messages. */
  entityLabel: string;
  /** Extra confirmation text, e.g. when the delete cascades. */
  confirmContent?: string;
  /** Extra cache tags to invalidate when the delete cascades. */
  alsoInvalidates?: MasterDataTagType[];
}

/**
 * Shared delete behaviour for the master data screens. They all go through the one
 * /masterData endpoint, which reports the outcome as a ResultDto: a refused delete
 * comes back as a 200 with Status 'Error' and a message explaining what still
 * references the row.
 */
export function useMasterDataDelete(options: UseMasterDataDeleteOptions) {
  const { entityType, tagType, entityLabel, confirmContent, alsoInvalidates } = options;

  const [deleteMasterData, { isLoading: isDeleting, error: deleteError }] =
    useDeleteMasterDataMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const runDelete = async (id: number, displayName?: string) => {
    try {
      setDeletingId(id);
      const response = await deleteMasterData({
        masterdata: {
          Id: id,
          entityType,
          updatedBy: sessionStorage.getItem('userEmail') || '',
          tagType,
          alsoInvalidates,
        },
      }).unwrap();

      const fallbackSuccess = displayName
        ? `${entityLabel} ${displayName} deleted`
        : `${entityLabel} deleted`;

      if (response?.status?.toLowerCase() === 'success') {
        message.success(toDisplayMessage(response?.message, fallbackSuccess));
      } else {
        message.error(toDisplayMessage(response?.message, `Failed to delete ${entityLabel}`));
      }
    } catch (error: any) {
      console.error(`Delete ${entityLabel} failed`, error);
      message.error(toDisplayMessage(error?.data ?? error, `Failed to delete ${entityLabel}`));
    } finally {
      setDeletingId(null);
    }
  };

  /** Opens the confirmation and deletes on confirm. */
  const deleteEntity = (id: number, displayName?: string) => {
    Modal.confirm({
      title: (
        <span>
          Delete {entityLabel} <b>{displayName}</b>?
        </span>
      ),
      content: confirmContent,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => runDelete(id, displayName),
    });
  };

  /** Error text for the screen level Alert, already coerced to a string. */
  const deleteErrorMessage = deleteError
    ? toDisplayMessage((deleteError as any)?.data ?? deleteError, `Failed to delete ${entityLabel}`)
    : undefined;

  return { deletingId, isDeleting, deleteError, deleteErrorMessage, deleteEntity };
}
